import type { ISettings, ThemeKey } from '../types';
import type { ThemeColorOverrides } from './cloudThemes';

import { requestMutation } from '../lib/fasterdom/fasterdom';
import themeColors from '../styles/themes.json';
import { animate } from './animation';
import { lerp } from './math';

type RGBAColor = {
  r: number;
  g: number;
  b: number;
  a?: number;
};

let isInitialized = false;
let themeColorOverrides: ThemeColorOverrides | undefined;

const DECIMAL_PLACES = 3;
const HEX_COLOR_REGEX = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i;
const DURATION_MS = 200;
const ENABLE_ANIMATION_DELAY_MS = 500;
export const THEME_COLORS_CHANGED_EVENT = 'theme-colors-changed';
const RGB_VARIABLES = new Set([
  '--color-text',
  '--color-primary-shade',
  '--color-text-secondary',
  '--color-accent-own',
]);
const THEME_KEYS: ThemeKey[] = ['light', 'dark'];

const DISABLE_ANIMATION_CSS = `
.no-animations #root *,
.no-animations #root *::before,
.no-animations #root *::after {
  transition: none !important;
}`;

const colors = (Object.keys(themeColors) as Array<keyof typeof themeColors>).map((property) => ({
  property,
  colors: {
    light: hexToRgb(themeColors[property][0]),
    dark: hexToRgb(themeColors[property][1]),
  },
}));

const injectCss = (css: string) => {
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
  return () => {
    document.head.removeChild(style);
  };
};

function dispatchThemeColorsChanged() {
  window.dispatchEvent(new Event(THEME_COLORS_CHANGED_EVENT));
}

export function setThemeColorOverrides(overrides?: ThemeColorOverrides) {
  themeColorOverrides = overrides;
}

export function getThemeColorOverride(theme: ThemeKey, property: string) {
  return themeColorOverrides?.[theme]?.[property];
}

const switchTheme = (theme: ISettings['theme'], withAnimation: boolean, shouldForce = false) => {
  const themeClassName = `theme-${theme}`;
  const isSameTheme = document.documentElement.classList.contains(themeClassName);
  if (isSameTheme && !shouldForce) {
    return;
  }

  const isDarkTheme = theme === 'dark';
  const shouldAnimate = isInitialized && withAnimation;
  const startTheme = isDarkTheme ? 'light' : 'dark';
  const endTheme = theme;
  const startAt = Date.now();
  const themeColorTag = document.querySelector('meta[name="theme-color"]');

  if (isSameTheme) {
    requestMutation(() => {
      updateThemeColorMeta(themeColorTag, theme);
      applyColorAnimationStep(theme, theme);
      dispatchThemeColorsChanged();
    });
    return;
  }

  requestMutation(() => {
    document.documentElement.classList.remove(`theme-${isDarkTheme ? 'light' : 'dark'}`);
    let uninjectCss: (() => void) | undefined;
    if (isInitialized) {
      uninjectCss = injectCss(DISABLE_ANIMATION_CSS);
      document.documentElement.classList.add('no-animations');
    }
    document.documentElement.classList.add(themeClassName);
    updateThemeColorMeta(themeColorTag, theme);

    setTimeout(() => {
      requestMutation(() => {
        uninjectCss?.();
        document.documentElement.classList.remove('no-animations');
      });
    }, ENABLE_ANIMATION_DELAY_MS);

    isInitialized = true;

    if (shouldAnimate) {
      animate(() => {
        const t = Math.min((Date.now() - startAt) / DURATION_MS, 1);

        applyColorAnimationStep(startTheme, endTheme, transition(t));
        if (t >= 1) {
          dispatchThemeColorsChanged();
        }

        return t < 1;
      }, requestMutation);
    } else {
      applyColorAnimationStep(startTheme, endTheme);
      dispatchThemeColorsChanged();
    }
  });
};

function transition(t: number) {
  return 1 - ((1 - t) ** 3.5);
}

export function hexToRgb(hex: string): RGBAColor {
  const result = HEX_COLOR_REGEX.exec(hex);
  if (!result) {
    return {
      r: 0,
      g: 0,
      b: 0,
    };
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
    a: result[4] !== undefined ? parseInt(result[4], 16) : undefined,
  };
}

export function lerpRgb(start: RGBAColor, end: RGBAColor, interpolationRatio: number): RGBAColor {
  const r = Math.round(lerp(start.r, end.r, interpolationRatio));
  const g = Math.round(lerp(start.g, end.g, interpolationRatio));
  const b = Math.round(lerp(start.b, end.b, interpolationRatio));
  const a = start.a !== undefined
    ? Math.round(lerp(start.a!, end.a!, interpolationRatio))
    : undefined;

  return {
    r, g, b, a,
  };
}

function applyColorAnimationStep(startTheme: ThemeKey, endTheme: ThemeKey, interpolationRatio: number = 1) {
  colors.forEach(({ property, colors: propertyColors }) => {
    const startColor = getColorForTheme(property, startTheme, propertyColors[startTheme]);
    const endColor = getColorForTheme(property, endTheme, propertyColors[endTheme]);

    const {
      r, g, b, a,
    } = lerpRgb(startColor, endColor, interpolationRatio);

    const roundedA = a !== undefined ? Math.round((a / 255) * 10 ** DECIMAL_PLACES) / 10 ** DECIMAL_PLACES : undefined;

    document.documentElement.style.setProperty(property, `rgb(${r},${g},${b}${roundedA ? `,${roundedA}` : ''})`);

    if (RGB_VARIABLES.has(property)) {
      document.documentElement.style.setProperty(`${property}-rgb`, `${r},${g},${b}`);
    }
  });
}

function getColorForTheme(property: string, theme: ThemeKey, fallbackColor: RGBAColor) {
  const overrideColor = themeColorOverrides?.[theme]?.[property];
  if (!overrideColor) {
    return fallbackColor;
  }

  return hexToRgb(overrideColor);
}

function updateThemeColorMeta(themeColorTag: Element | null, theme: ThemeKey) {
  if (!themeColorTag) {
    return;
  }

  const fallbackBackground = themeColors['--color-background'][THEME_KEYS.indexOf(theme)];
  const backgroundColor = themeColorOverrides?.[theme]?.['--color-background'] || fallbackBackground;

  themeColorTag.setAttribute('content', normalizeMetaColor(backgroundColor));
}

function normalizeMetaColor(color: string) {
  return color.length === 9 ? color.slice(0, 7) : color;
}

export default switchTheme;
